import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import { Modal } from 'antd';
import styled from 'styled-components';

import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AccessLevelDetailsModalProps } from '@/widgets/modals/types';

import { AccessLevelPreview } from './AccessLevelPreview';

export const AccessLevelDetailsModal: FC<AccessLevelDetailsModalProps> = ({ level }) => {
  const { t } = useTranslation('widgets/modals', {
    keyPrefix: `accessLevelDetails.${level.toLowerCase()}`,
  });
  const { t: tc } = useTranslation('common');

  const isDesktop = useIsDesktop();
  const modals = useModalsStore();

  return (
    <StyledModal
      open
      centered={isDesktop}
      footer={null}
      width={800}
      closable={true}
      maskClosable={false}
      onCancel={modals.closeModal}
    >
      <AccessLevelPreview level={level} />
      <Container>
        <Content>
          <Title size={22} bold>
            {t('title')}
          </Title>
          <span>{t('description')}</span>
          <div>
            <Title size={14} bold>
              {t('allowsTitle')}
            </Title>
            <List>
              <li>{t('allows1')}</li>
              <li>{t('allows2')}</li>
              <li>{t('allows3')}</li>
            </List>
          </div>
        </Content>
        <Buttons>
          <Button size="middle" onClick={modals.closeModal}>
            {tc('done')}
          </Button>
        </Buttons>
      </Container>
    </StyledModal>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 8px;
  width: 100%;
  padding: 24px 0;
  ${media.sm} {
    padding: 16px 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px 16px 24px;

  ${media.sm} {
    padding: 0 16px 16px 16px;
  }
`;

const List = styled.ul`
  list-style-position: inside;
`;

const StyledModal = styled(Modal)`
  &&& {
    & > div,
    .ant-modal-content {
      height: inherit;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .ant-modal-body {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .ant-modal-content {
      padding: 0;
      color: ${COLORS.systemDark300};
    }

    ${media.sm} {
      height: 100%;
      position: fixed;
      inset: 0;

      .ant-modal-content {
        height: fit-content;
        margin-top: auto;
      }
    }
  }
`;
